// ui/summary.ts — the itemised run summary (DESIGN §8).
//
// "Zones cleared w/ times vs par · kills by family · embers earned (breakdown) ·
//  crests earned (which deed) · feats advanced · 'Next: you can afford —'."
//
// Everything here is read back from game/runstats.ts, which accumulates as the
// run happens. A losing run gets the same page as a winning one: this is the
// only screen that tells the player what the last four minutes were worth.

import type { RunSummary } from '../game/meta'
import { meta } from '../game/meta'
import { ZONE_PAR_SECONDS, parBand, runStats } from '../game/runstats'
import { FEAT_BY_ID } from '../data/feats'
import { RELICS } from '../data/relics'

let root: HTMLElement | null = null
let panel: HTMLElement | null = null

function ui(): HTMLElement {
  if (!root) root = document.getElementById('ui') as HTMLElement
  return root
}

function fmtTime(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

const BAND_LABEL: Record<'under' | 'in' | 'over', string> = {
  under: 'under par',
  in: 'on par',
  over: 'over par',
}

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V']

function zoneRows(): string {
  if (runStats.zones.length === 0) return '<div class="sum-empty">No zone entered.</div>'
  return runStats.zones
    .map((z) => {
      const band = parBand(z.seconds)
      const mark = z.cleared ? '<span class="ok">cleared</span>' : '<span class="dim">fell here</span>'
      return `<div class="sum-line">
        <span class="n">Zone ${z.zone} — ${esc(z.name)}</span>
        <span class="v">${fmtTime(z.seconds * 1000)} <span class="band ${band}">${BAND_LABEL[band]}</span></span>
        <span class="s">${mark}</span>
      </div>`
    })
    .join('')
}

function killRows(): string {
  const entries = Object.entries(runStats.killsByFamily).sort((a, b) => b[1] - a[1])
  if (entries.length === 0) return '<div class="sum-empty">Nothing fell to you.</div>'
  const max = entries[0][1] || 1
  return entries
    .map(
      ([name, n]) => `<div class="sum-bar">
        <span class="n">${esc(name)}</span>
        <span class="track"><i style="width:${Math.max(4, Math.round((n / max) * 100))}%"></i></span>
        <span class="v">${n}</span>
      </div>`,
    )
    .join('')
}

function emberRows(): string {
  const e = runStats.embers
  const rows: [string, number][] = [
    ['Kills', e.kills],
    ['Rooms cleared', e.rooms],
    ['Vaults', e.vault],
    ['Zone / boss payout', e.boss],
    ['Other', e.other],
  ]
  return rows
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `<div class="k">${k}</div><div class="v ember">+${n}</div>`)
    .join('')
}

/**
 * Feats advanced (DESIGN §8: "bars moving on-screen"). Reads the per-run gain
 * meta kept while the run happened, so a losing run shows exactly what it was
 * worth. A hidden feat that is still under half stays '???' here too.
 */
function featRows(): string {
  const entries = Object.entries(meta.runFeatGain).sort((a, b) => b[1] - a[1])
  if (entries.length === 0) return '<div class="sum-empty">No deed advanced.</div>'
  const out: string[] = []
  for (const [featId, gain] of entries) {
    const f = FEAT_BY_ID.get(featId)
    if (!f) continue
    const prog = Math.min(f.target, meta.featProgress(featId))
    const done = meta.featDone(featId)
    const revealed = meta.featRevealed(f)
    const pct = Math.max(0, Math.min(100, Math.round((prog / f.target) * 100)))
    const was = Math.max(0, prog - gain)
    const wasPct = Math.max(0, Math.min(100, Math.round((was / f.target) * 100)))
    out.push(`<div class="sum-feat${done ? ' is-done' : ''}">
        <span class="n">${revealed ? esc(f.name) : '???'}</span>
        <span class="track"><i style="width:${revealed ? pct : 0}%"></i></span>
        <span class="v">${
          revealed
            ? done
              ? `COMPLETE · +${f.crests} crest${f.crests === 1 ? '' : 's'}`
              : `${wasPct}% → ${pct}% (${prog}/${f.target})`
            : `+${gain} toward something`
        }</span>
      </div>`)
  }
  if (out.length === 0) return '<div class="sum-empty">No deed advanced.</div>'
  return out.join('')
}

/** Which deeds paid Crests during this run (DESIGN §8: "which deed"). */
function crestRows(): string {
  if (meta.runCrests.length === 0) {
    return `<div class="sum-empty">No deed paid out this run. Banked: ${meta.data.crests} crests.</div>`
  }
  const total = meta.runCrests.reduce((n, c) => n + c.crests, 0)
  const rows = meta.runCrests
    .map(
      (c) =>
        `<div class="sum-line"><span class="n">${esc(c.name)}</span><span class="v sum-crest">+${c.crests}</span><span class="s dim">deed complete</span></div>`,
    )
    .join('')
  return `${rows}<div class="sum-line"><span class="n dim">Crests earned this run</span><span class="v sum-crest">+${total}</span><span class="s dim">banked: ${meta.data.crests}</span></div>`
}

/**
 * "Next: you can afford —" (DESIGN §8). Names the cheapest shrine rank the
 * banked embers now reach; failing that, the cheapest one still ahead.
 */
function nextNodeHtml(): string {
  const afford = meta.cheapestNode(true)
  if (afford) {
    const name =
      afford.node.ranks > 1
        ? `${afford.node.name} ${ROMAN[Math.min(afford.rank, ROMAN.length - 1)]}`
        : afford.node.name
    return `You can now afford: <b>${esc(name)} — ${afford.cost} embers</b><br />
      <span class="dim">${esc(afford.node.desc)} · banked ${meta.data.embers} embers · ${meta.data.crests} crests</span>`
  }
  const next = meta.cheapestNode(false)
  if (next) {
    const name =
      next.node.ranks > 1
        ? `${next.node.name} ${ROMAN[Math.min(next.rank, ROMAN.length - 1)]}`
        : next.node.name
    const short = next.cost - meta.data.embers
    return `Next at the shrine: <b>${esc(name)} — ${next.cost} embers</b>
      <span class="dim">(${short} more)</span><br />
      <span class="dim">Banked ${meta.data.embers} embers · ${meta.data.crests} crests</span>`
  }
  return `The shrine is fully woken. Banked: <b>${meta.data.embers} embers</b> · ${meta.data.crests} crests`
}

export function showSummary(s: RunSummary, onRestart: () => void, onHub: () => void): void {
  hideSummary()
  const el = document.createElement('div')
  el.className = 'screen'
  el.id = 'summary'
  const cleared = runStats.zonesCleared
  const fullWin = !s.died && !s.abandoned && s.zone >= 6
  const title = s.died
    ? 'THE DEEP TAKES YOU'
    : s.abandoned
      ? 'YOU CLIMB BACK OUT'
      : fullWin
        ? 'THE DEEP WILL REMEMBER'
        : 'THE GATE IS BROKEN'
  const sub = s.died
    ? `Fell in ${esc(s.zoneName)}. Nothing banked is ever lost.`
    : s.abandoned
      ? `The descent ends by your hand, in ${esc(s.zoneName)}. Everything banked stays banked.`
      : fullWin
        ? `The Gloom recoils. Hollowdeep is yours to the bottom — this descent is complete.`
        : `Zone ${s.zone} cleared — ${esc(s.zoneName)}. The stair below is not yet cut.`

  const relicDesc = new Map(RELICS.map((r) => [r.name, r.desc]))
  const relics = runStats.relics.length
    ? runStats.relics
        .map((r) => `<span class="chip" title="${esc(relicDesc.get(r) ?? '')}">${esc(r)}</span>`)
        .join('')
    : '<span class="dim">none taken</span>'

  el.innerHTML = `
    <h1>${title}</h1>
    <p class="sub">${sub}</p>

    <h3 class="sum-h">ZONES <span class="dim">— par ${ZONE_PAR_SECONDS[0] / 60}–${ZONE_PAR_SECONDS[1] / 60} min each</span></h3>
    <div class="sum-block">${zoneRows()}</div>

    <h3 class="sum-h">KILLS BY FAMILY <span class="dim">— ${s.kills} total</span></h3>
    <div class="sum-block">${killRows()}</div>

    <h3 class="sum-h">EMBERS EARNED</h3>
    <div class="rows">
      ${emberRows()}
      <div class="row-sep"></div>
      <div class="k">Total banked this run</div><div class="v ember">+${s.embers}</div>
    </div>

    <h3 class="sum-h">THE RUN</h3>
    <div class="rows">
      <div class="k">Zones cleared</div><div class="v">${cleared}</div>
      <div class="k">Rooms cleared</div><div class="v">${s.roomsCleared}</div>
      <div class="k">Vaults opened</div><div class="v">${runStats.vaultsOpened}</div>
      <div class="k">Generators cut</div><div class="v">${runStats.generatorsDown}</div>
      <div class="k">Run time</div><div class="v">${fmtTime(s.timeMs)}</div>
      <div class="row-sep"></div>
      <div class="k">Gold ended with <span class="dim">(lost)</span></div><div class="v">${s.gold}</div>
      <div class="k">Gold spent at altars</div><div class="v">${runStats.goldSpent}</div>
    </div>

    <h3 class="sum-h">RELICS TAKEN</h3>
    <div class="sum-block chips">${relics}</div>

    <h3 class="sum-h">CRESTS EARNED</h3>
    <div class="sum-block">${crestRows()}</div>

    <h3 class="sum-h">FEATS ADVANCED</h3>
    <div class="sum-block">${featRows()}</div>

    <!-- SHRINE CALLOUT (DESIGN §8) — the cheapest newly-affordable node.
         Keep the id: it is the agreed handle. -->
    <div class="banked" id="summary-next-node">${nextNodeHtml()}</div>

    <div class="sum-actions">
      <button class="btn" id="summary-hub">RETURN TO THE THRESHOLD</button>
      <button class="btn" id="summary-restart">DESCEND AGAIN  (R)</button>
    </div>
    <div class="prompt">PRESS R TO DESCEND AGAIN</div>
  `
  ui().appendChild(el)
  panel = el
  const btn = el.querySelector('#summary-restart') as HTMLButtonElement | null
  btn?.addEventListener('click', () => onRestart())
  const hub = el.querySelector('#summary-hub') as HTMLButtonElement | null
  hub?.addEventListener('click', () => onHub())
}

export function hideSummary(): void {
  if (panel && panel.parentElement) panel.parentElement.removeChild(panel)
  panel = null
}

export function summaryVisible(): boolean {
  return panel != null
}
